import { Spin, Typography } from 'antd';

/** The parts of a list query (Refine's `tableQuery`) the total reads. */
export interface ListTotalQuery {
  data?: { total?: number };
  isFetching: boolean;
  isError: boolean;
  /** Refine keeps the last answer while new filters load; that total is for the old filters. */
  isPreviousData?: boolean;
  /** Paused: a retry waits while the tab is in the background, so no new answer is coming yet. */
  isPaused?: boolean;
}

interface ListTotalProps {
  query: ListTotalQuery;
  singular: string;
  plural: string;
}

/**
 * How many items the current filters match, across every page, shown next to the filters. Only
 * a total that is the answer for these filters is shown: never the previous filters' total while
 * new ones load (a spinner instead), while a retry waits, or after a failed read.
 */
export const ListTotal: React.FC<ListTotalProps> = ({ query, singular, plural }) => {
  if (query.isFetching) return <Spin size="small" aria-label={`Counting ${plural}`} />;
  if (query.isError || query.isPreviousData || query.isPaused) return null;
  const total = query.data?.total;
  if (typeof total !== 'number') return null;
  return (
    <Typography.Text type="secondary" aria-live="polite" data-testid="list-total">
      {total.toLocaleString('en-GB')} {total === 1 ? singular : plural}
    </Typography.Text>
  );
};
